/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Directive, EventEmitter, HostBinding, HostListener, Input, Output} from '@angular/core';
import * as XLSX from 'xlsx';

export interface FileHandle {
  file: File;
  url: string;
  data?: ArrayBuffer;
}

@Directive({
    selector: '[appDrag]',
    standalone: false
})
export class DragDirective {
  @Input() apiUrl: string;
  @Output() files: EventEmitter<Map<string, any>> = new EventEmitter();

  @HostBinding('style.background') private background = '#FFF';

  constructor() {
    // Sonar fix: This is intentional
  }

  /**
   * Change color of drag container
   * @param evt drag event
   */
  @HostListener('dragover', ['$event'])
  public onDragOver(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.background = '#999';
  }

  /**
   * Change color of drag container
   * @param evt drag event
   */
  @HostListener('dragleave', ['$event'])
  public onDragLeave(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.background = '#FFF';
  }

  /**
   * Change color of drag container
   * Get files and sheets
   * @param evt drag event
   */
  @HostListener('drop', ['$event'])
  public onDrop(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.background = '#FFF';
    const files: FileHandle[] = [];
    for (let i = 0; i < evt.dataTransfer?.files.length; i++) {
      const file = evt.dataTransfer?.files[i];
      const url = this.createImageFromBlob(file);
      files.push({file, url});
    }

    if (files.length > 0) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {type: 'array'});
        const sheets = workbook.SheetNames;
        const event = new Map();
        event.set('file', files[0].file);
        event.set('data', sheets);
        this.files.emit(event);
      };
      reader.readAsArrayBuffer(files[0].file);
    }
  }

  /**
   * Converts a File object to a data URL string.
   * @param image - The File object to convert.
   * @returns The data URL string representing the image.
   */
  createImageFromBlob(image: File): string {
    const reader = new FileReader(); // Create a new instance of FileReader
    let url = ''; // Variable to store the resulting data URL

    /**
     * Event listener for when the FileReader has finished loading the file.
     * @param event - The load event.
     */
    reader.addEventListener('load', () => {
      url = reader.result as string; // Store the data URL in the 'url' variable
    }, false);

    if (image) {
      reader.readAsDataURL(image); // Start reading the image file as a data URL
    }

    return url; // Return the resulting data URL (initially empty, will be populated after the FileReader finishes loading)
  }
}
